/**
 * ReviewThumbnailList - Virtualized thumbnail list for image review.
 *
 * Left panel of the review split-pane. Shows:
 * - Filter pills at top
 * - Virtualized list of thumbnails
 * - Status dots on each thumbnail
 * - Highlight for selected image
 * - Bulk selection checkboxes (when in bulk mode)
 *
 * Uses @tanstack/react-virtual for smooth performance with large galleries.
 *
 * @see /src/features/gallery/pages/GalleryReviewPage.tsx - Parent component
 */

import { useRef, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { CheckCircle, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import { StatusDot } from '../shared/GalleryStatusBadge'
import { ReviewFilterPills, type ReviewFilter } from './ReviewFilterPills'
import { ReviewProgressBar } from './ReviewProgressBar'
import type { GalleryAllImages } from '../../types'
import type { ImageStats } from '../../context/GalleryDetailContext'

interface ReviewThumbnailListProps {
  /** All images (will be filtered by component) */
  images: GalleryAllImages[]
  /** Current filter */
  filter: ReviewFilter
  /** Called when filter changes */
  onFilterChange: (filter: ReviewFilter) => void
  /** Currently selected image ID */
  selectedId: number | null
  /** Called when image is selected */
  onSelect: (id: number) => void
  /** Statistics for filter counts */
  stats: ImageStats
  /** Enable bulk selection mode */
  bulkMode?: boolean
  /** Selected IDs in bulk mode */
  bulkSelectedIds?: number[]
  /** Called when bulk selection toggled */
  onBulkToggle?: (id: number) => void
  /** Additional className */
  className?: string
}

/**
 * Filter images based on current filter selection.
 */
function filterImages(images: GalleryAllImages[], filter: ReviewFilter): GalleryAllImages[] {
  // Only show job images in review (direct photos don't need review)
  const jobImages = images.filter((img) => img.image_type === 'job_image')

  if (filter === 'all') return jobImages
  return jobImages.filter((img) => img.review_status === filter)
}

/**
 * Single thumbnail item component.
 */
function ThumbnailItem({
  image,
  isSelected,
  onClick,
  bulkMode,
  isChecked,
  onCheckChange,
}: {
  image: GalleryAllImages
  isSelected: boolean
  onClick: () => void
  bulkMode: boolean
  isChecked: boolean
  onCheckChange: () => void
}) {
  return (
    <div
      role="option"
      aria-selected={isSelected}
      onClick={() => !bulkMode && onClick()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (bulkMode) {
            onCheckChange()
          } else {
            onClick()
          }
        }
      }}
      tabIndex={0}
      className={cn(
        'group relative p-2 cursor-pointer transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        isSelected
          ? 'bg-primary/10 ring-2 ring-primary ring-inset'
          : 'hover:bg-muted/50'
      )}
    >
      <div
        className={cn(
          'relative aspect-square rounded-md overflow-hidden bg-muted',
          // Subtle background tint based on status
          image.review_status === 'approved' && 'bg-success/10',
          image.review_status === 'rejected' && 'bg-destructive/10',
          image.review_status === 'pending' && 'bg-warning/10'
        )}
      >
        {/* Image */}
        <img
          src={image.image_url ?? ''}
          alt={image.caption ?? `Image ${image.original_id}`}
          loading="lazy"
          className={cn(
            'w-full h-full object-cover transition-transform duration-200',
            'group-hover:scale-105',
            // Add border styling for rejected images
            image.review_status === 'rejected' && 'border-2 border-dashed border-destructive'
          )}
        />

        {/* Status dot - top left */}
        <div className="absolute top-1.5 left-1.5">
          <StatusDot status={image.review_status} size="md" />
        </div>

        {/* Cover badge - bottom left to avoid overlap with status dot */}
        {image.is_cover && (
          <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 bg-primary text-primary-foreground text-[10px] font-semibold rounded shadow-sm">
            COVER
          </div>
        )}

        {/* Approved status indicator - checkmark overlay */}
        {image.review_status === 'approved' && !bulkMode && (
          <div className="absolute bottom-1.5 right-1.5">
            <CheckCircle className="h-6 w-6 text-success fill-success/20 stroke-2 drop-shadow-md" />
          </div>
        )}

        {/* Rejected status indicator - X overlay */}
        {image.review_status === 'rejected' && !bulkMode && (
          <div className="absolute bottom-1.5 right-1.5">
            <XCircle className="h-6 w-6 text-destructive fill-destructive/20 stroke-2 drop-shadow-md" />
          </div>
        )}

        {/* Checkbox overlay in bulk mode */}
        {bulkMode && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-black/20"
            onClick={(e) => {
              e.stopPropagation()
              onCheckChange()
            }}
          >
            <Checkbox
              checked={isChecked}
              onCheckedChange={() => onCheckChange()}
              className="h-6 w-6 border-2 border-white bg-white/80 data-[state=checked]:bg-primary"
            />
          </div>
        )}

        {/* Selected indicator */}
        {isSelected && !bulkMode && (
          <div className="absolute inset-0 ring-2 ring-primary ring-inset rounded-md" />
        )}
      </div>

      {/* Image info below - removed job number as it's redundant */}
      <div className="mt-1.5 px-0.5">
        <div className="text-xs text-muted-foreground truncate">
          {/* Empty space for future use or remove entirely */}
        </div>
      </div>
    </div>
  )
}

export function ReviewThumbnailList({
  images,
  filter,
  onFilterChange,
  selectedId,
  onSelect,
  stats,
  bulkMode = false,
  bulkSelectedIds = [],
  onBulkToggle,
  className,
}: ReviewThumbnailListProps) {
  const parentRef = useRef<HTMLDivElement>(null)

  // Filter images
  const filteredImages = filterImages(images, filter)

  // Virtual list setup - 2 columns
  const columnCount = 2
  const rowCount = Math.ceil(filteredImages.length / columnCount)

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120, // Estimated row height (thumbnail + padding)
    overscan: 3,
  })

  // Scroll selected image into view
  useEffect(() => {
    if (selectedId === null) return

    const index = filteredImages.findIndex((img) => img.original_id === selectedId)
    if (index === -1) return

    const rowIndex = Math.floor(index / columnCount)
    virtualizer.scrollToIndex(rowIndex, { align: 'center' })
  }, [selectedId, filteredImages, virtualizer, columnCount])

  // Calculate review progress
  const reviewed = stats.approved + stats.rejected
  const total = stats.jobImages

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header with filters */}
      <div className="p-4 space-y-4 border-b bg-background">
        <ReviewFilterPills
          activeFilter={filter}
          onFilterChange={onFilterChange}
          stats={stats}
        />
        <ReviewProgressBar
          reviewed={reviewed}
          total={total}
          showDetails
          breakdown={{
            approved: stats.approved,
            rejected: stats.rejected,
            pending: stats.pending,
          }}
        />
      </div>

      {/* Thumbnail grid */}
      <div
        ref={parentRef}
        className="flex-1 overflow-auto"
        role="listbox"
        aria-label="Images to review"
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const startIndex = virtualRow.index * columnCount
            const rowImages = filteredImages.slice(startIndex, startIndex + columnCount)

            return (
              <div
                key={virtualRow.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                className="grid grid-cols-2 gap-1 px-2"
              >
                {rowImages.map((image) => {
                  // Skip images without IDs (shouldn't happen but safety check)
                  if (image.original_id === null) return null
                  return (
                    <ThumbnailItem
                      key={image.original_id}
                      image={image}
                      isSelected={selectedId === image.original_id}
                      onClick={() => onSelect(image.original_id!)}
                      bulkMode={bulkMode}
                      isChecked={bulkSelectedIds.includes(image.original_id)}
                      onCheckChange={() => onBulkToggle?.(image.original_id!)}
                    />
                  )
                })}
              </div>
            )
          })}
        </div>

        {/* Empty state */}
        {filteredImages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full py-12 text-center">
            <p className="text-muted-foreground">
              {filter === 'all'
                ? 'No images to review'
                : `No ${filter.replace('_', ' ')} images`}
            </p>
          </div>
        )}
      </div>

      {/* Footer with count */}
      <div className="p-3 border-t bg-muted/30 text-xs text-muted-foreground text-center">
        {filteredImages.length} image{filteredImages.length !== 1 ? 's' : ''}
        {bulkMode && bulkSelectedIds.length > 0 && (
          <span className="font-medium text-foreground ml-2">
            ({bulkSelectedIds.length} selected)
          </span>
        )}
      </div>
    </div>
  )
}
